import cx from 'classnames2';
import { uniqueId as _uniqueId } from 'lodash-es';
import React, { useState } from 'react';

import { IBaseProps } from '../utils';

import { LoadingBlock } from '../LoadingBlock';

import styles from './ImageCard.module.scss';

export interface IImageCardProps extends IBaseProps {
  /** Text for card title */
  title: string;
  /** URL of image/illustration to use in card. */
  imageSrc?: string;
  /** alt of image/illustration to use in card. */
  imageAlt?: string;
  /** A URL to navigate to. Will not be used if onClick is defined. */
  href?: string;
  /** Text for card category (shown above title). */
  category?: string;
  /** Maximum height for the image (units required; i.e., `50px` not `50`).
   * Image will always scale to width of container, illustrations scale based on their own properties.
   */
  maxImageHeight?: string;
  maxContentLines?: number;
  /** An Icon component displayed on the left of title */
  titleIcon?: React.ReactNode;
  /** an `<img>`, `<svg>`, or `<Illustration>` node containing the image to use in the card. */
  illustration?: React.ReactNode;
  imageOnTop?: boolean;
  isLoading?: boolean;
  loadingImageVariant?: 'default' | 'large';
  /** A function that is called on click event */
  onClick?(): void;
  /** A function that is called on focus event */
  onFocus?(): void;
  /** A function that is called on mouse over event */
  onHover?(): void;
  /** A function that is called on blur event */
  onBlur?(): void;
}

export const ImageCardBase: React.FC<IImageCardProps> = (props) => {
  const Element = props.href ? 'a' : 'button';
  const [id] = useState(_uniqueId('imagecard-'));
  const handleKeyPress = (event: React.KeyboardEvent<any>): void => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      if (props.onClick) {
        props.onClick();
      }
      if (props.href) {
        window.location.href = props.href;
      }
    }
  };

  const imageSize = props.loadingImageVariant === 'default' ? 'default' : 'large';

  const imgElement = props.isLoading ? (
    <div className={styles.loadingWrapper}>
      <LoadingBlock variant="image" imageSize={imageSize} className={styles.loadingImage} />
    </div>
  ) : (
    <>
      {props.imageSrc && (
        <div
          className={styles.image}
          style={{ maxHeight: props.maxImageHeight ? props.maxImageHeight : 'auto' }}
          data-test="image-frame"
        >
          <img className={styles.imageElement} src={props.imageSrc} alt={props.imageAlt} data-test="image" />
        </div>
      )}
      {props.illustration && (
        <div className={styles.image} data-test="image-frame">
          {props.illustration}
        </div>
      )}
    </>
  );

  const title = props.isLoading ? (
    <div className={styles.loadingWrapper}>
      <LoadingBlock className={styles.loadingTitle} />
    </div>
  ) : props.titleIcon ? (
    <>
      <div className={styles.icon}>{props.titleIcon}</div>
      <div>{props.title}</div>
    </>
  ) : (
    props.title
  );

  let maxLinesClass = ''; // Default to nothing
  const maxLineStyle: React.CSSProperties = {}; // Default to nothing
  if (props.maxContentLines) {
    maxLinesClass = styles.maxLines;
    maxLineStyle.lineClamp = props.maxContentLines;
    maxLineStyle.WebkitLineClamp = props.maxContentLines;
  }

  const content = props.isLoading ? (
    <div className={styles.loadingWrapper}>
      <LoadingBlock className={styles.loadingContent} />
    </div>
  ) : (
    props.children && (
      <div data-test="content" id={id} className={cx(maxLinesClass)} style={maxLineStyle}>
        {props.children}
      </div>
    )
  );

  return (
    <Element
      aria-label={props.title}
      aria-describedby={props.category ? `${id}-category ${id}` : id}
      className={cx(styles.basicCard, props.className)}
      href={props.href}
      tabIndex={0}
      data-test={props.dataTest}
      onClick={props.onClick}
      onFocus={props.onFocus}
      onMouseOver={props.onHover}
      onKeyPress={handleKeyPress}
    >
      <div className={styles.imageView}>
        {props.imageOnTop && imgElement}
        <div className={styles.imageText}>
          {props.category && (
            <div className={styles.category} data-test="category" id={`${id}-category`}>
              {props.category}
            </div>
          )}
          <div className={styles.title} data-test="title" id={`${id}-title`}>
            {title}
          </div>
          {content}
        </div>
        {!props.imageOnTop && imgElement}
      </div>
    </Element>
  );
};
